import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { Button, Checkbox, Col, Row, Tooltip, TextField } from '@arkestro/arkestro-design-system';
import { InfoCircleFilled } from '@ant-design/icons';
import useTranslation from 'next-translate/useTranslation';

import { UserContext } from '@providers/UserProvider/UserProvider';
import { emailParts } from '@utils/email';
import { isValidSupplierOrgName } from '@utils/names';
import Footer from '@components/on_board/Footer/Footer';

import style from './CreateNewOrgFields.module.scss';

const CreateNewOrgFields = ({ currentStepName, updateOnboardStep, updateOrgId, progress, setProperty, isFreeEmail = true, newVendorOrgParams }) => {
  const { t } = useTranslation('common');
  const currentUser = useContext(UserContext);

  const [name, setName] = useState(newVendorOrgParams?.name || '');
  const [description, setDescription] = useState(newVendorOrgParams?.description || '');
  const [website, setWebsite] = useState(newVendorOrgParams?.website || '');
  const [publicListed, setPublicListed] = useState(newVendorOrgParams?.publicListed || true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isFormValid = isValidSupplierOrgName(name) && description.length > 0;

  const goBack = () => {
    setProperty({ currentOrgStep: 'confirm_affiliation', newVendorOrgParams: undefined, vendorOrg: undefined });
  };

  const handleChange = event => {
    let setFn;

    if (event.target.name === 'publicListed') {
      setPublicListed(event.target.checked);
      return;
    }

    switch (event.target.name) {
      case 'website':
        setFn = setWebsite; break;
      case 'description':
        setFn = setDescription; break;
      default:
        setFn = setName; break;
    }

    setFn(event.target.value);
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (!isFormValid || isSubmitting) return;

    const parts = isFreeEmail ? {} : emailParts(currentUser.email);
    const { domain, tld } = parts;
    const addDomain = (domain && tld)
      ? {
        name: domain,
        url: domain.concat('.').concat(tld)
      }
      : null;

    const data = { description, name, publicListed, website };

    if (updateOrgId) data.vendorOrgId = updateOrgId;
    if (addDomain) data.domains = [addDomain];

    setIsSubmitting(true);

    setProperty({ newVendorOrgParams: data });
    updateOnboardStep(currentStepName); // updates onboarding state, which advances to next step
    setIsSubmitting(false);
  };

  return (
    <>
      <div className={style.titleContainer}>
        <div className={style.title}>{t('onboarding.supplier.create_profile')}</div>
        <div className={style.secondTitle}>
          {t('onboarding.supplier.create_profile_description')}
        </div>
      </div>

      <form className={style.form} onSubmit={handleSubmit}>
        <section style={{ marginTop: 30 }}>
          <Row gutter={16}>
            <Col sm={24} style={{ marginBottom: 15 }}>
              <TextField
                label={t('onboarding.supplier.organization_name')}
                name='name'
                onChange={handleChange}
                required
                tooltipText={t('tooltip.new_org.organization_name_visible')}
                value={name}
              />
            </Col>
            <Col sm={24} style={{ marginBottom: 15 }}>
              <TextField
                label={t('onboarding.supplier.organization_description')}
                name='description'
                onChange={handleChange}
                required
                textArea
                tooltipText={t('tooltip.new_org.tell_prospective')}
                value={description}
              />
            </Col>
            <Col sm={24}>
              <TextField
                label={t('onboarding.supplier.organization_website')}
                name='website'
                onChange={handleChange}
                value={website}
              />
            </Col>
          </Row>
        </section>
        <section style={{ marginTop: 30 }}>
          <Row gutter={16}>
            <Col sm={24}>
              <Checkbox checked={publicListed} name='publicListed' onChange={handleChange}>
                {t('onboarding.supplier.add_organization_to_directory')}
                <Tooltip
                  overlayStyle={{ minWidth: 300 }}
                  placement='bottom'
                  title={t('tooltip.new_org.public_listed')}
                >
                  <InfoCircleFilled style={{ fontSize: 12, marginLeft: 10 }} />
                </Tooltip>
              </Checkbox>
            </Col>
          </Row>
        </section>
        <div className={style.buttons}>
          <Button
            onClick={goBack}
          >
            {t('general.back')}
          </Button>
          <Button
            disabled={!isFormValid || isSubmitting}
            htmlType='submit'
            loading={progress}
            type='primary'
          >
            {t('general.continue')}
          </Button>
        </div>
      </form>
      <Footer />
    </>
  );
};

CreateNewOrgFields.propTypes = {
  currentStepName: PropTypes.string.isRequired,
  isFreeEmail: PropTypes.bool,
  progress: PropTypes.bool.isRequired,
  updateOnboardStep: PropTypes.func.isRequired,
  updateOrgId: PropTypes.number
};

export default CreateNewOrgFields;
